/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

/**
 * Shared input validation utilities for tracking parameters
 *
 * This module protects against DoS attacks via oversized URL parameters
 * that get stored in cookies and sent with every HTTP request.
 *
 * Used by both:
 * - Server-side: Validates parameters before serving HTML
 * - Client-side: Validates parameters before GTM processing and API calls
 */

// Maximum safe lengths for tracking parameters
export const MAX_LENGTHS = {
  UTM_PARAM: 250, // Standard UTM parameters (utm_source, utm_campaign, etc.)
  TRACKING_PARAM: 500, // Other marketing parameters (rogue, elektra, etc.)
  QUERY_PARAM: 1000, // General query parameters
  COOKIE_VALUE: 2000, // Maximum cookie value size (leave buffer for cookie name)
} as const

/**
 * List of marketing/tracking parameter names that should be validated
 * These correspond to the marketoParams used throughout the application
 */
const TRACKING_PARAM_NAMES = [
  // Marketo custom parameters
  'ultron',
  'blade',
  'hulk',
  'gambit',
  'thor',
  'camp',
  'src',
  'mdm',
  'cnt',
  'trm',
  'baymax',
  'storm',
  'rogue',
  'elektra',
  'tech',
  'plcmt',
  'cta',
  'pg',
  // Standard UTM parameters
  'utm_campaign',
  'utm_source',
  'utm_medium',
  'utm_term',
  'utm_content',
  'utm_id',
] as const

/**
 * Truncates a string to a maximum length with optional suffix
 *
 * @param value - The string to truncate
 * @param maxLength - Maximum allowed length
 * @param suffix - Optional suffix to append (default: empty string)
 * @returns Truncated string or undefined if input is undefined
 */
export function truncateString(
  value: string | undefined,
  maxLength: number,
  suffix: string = '',
): string | undefined {
  if (!value) {
    return value
  }

  if (value.length <= maxLength) {
    return value
  }

  return value.substring(0, maxLength - suffix.length) + suffix
}

/**
 * Sanitizes tracking parameter values to prevent DoS attacks
 *
 * @param value - The parameter value to sanitize
 * @param maxLength - Maximum allowed length (default: TRACKING_PARAM)
 * @returns Sanitized string or undefined if input is invalid
 */
export function sanitizeTrackingParam(
  value: unknown,
  maxLength: number = MAX_LENGTHS.TRACKING_PARAM,
): string | undefined {
  if (typeof value !== 'string') {
    return undefined
  }

  // Remove potentially malicious characters
  const sanitized = value.replace(/[<>"']/g, '').trim()

  if (!sanitized) {
    return undefined
  }

  // Enforce length limits
  return truncateString(sanitized, maxLength, '')
}

/**
 * Validates and sanitizes an object of tracking parameters
 *
 * @param params - Object containing tracking parameters
 * @param maxLength - Maximum allowed length per parameter
 * @returns Sanitized object with only valid parameters
 */
export function sanitizeTrackingParams<T extends Record<string, unknown>>(
  params: T,
  maxLength: number = MAX_LENGTHS.TRACKING_PARAM,
): Partial<T> {
  const sanitized: Partial<T> = {}

  for (const [key, value] of Object.entries(params)) {
    const sanitizedValue = sanitizeTrackingParam(value, maxLength)

    if (sanitizedValue) {
      sanitized[key as keyof T] = sanitizedValue as T[keyof T]
    }
  }

  return sanitized
}

/**
 * Checks if a query parameter is a tracking parameter that needs validation
 */
export function isTrackingParam(paramName: string): boolean {
  return TRACKING_PARAM_NAMES.includes(paramName as any)
}

/**
 * Determines the appropriate max length for a parameter based on its name
 */
export function getMaxLengthForParam(paramName: string): number {
  if (paramName.startsWith('utm_')) {
    return MAX_LENGTHS.UTM_PARAM
  }

  if (isTrackingParam(paramName)) {
    return MAX_LENGTHS.TRACKING_PARAM
  }

  return MAX_LENGTHS.QUERY_PARAM
}

/**
 * Result of query parameter sanitization
 */
export interface SanitizeQueryParamsResult {
  sanitized: Record<string, string | string[]>
  modified: boolean
  violations: Array<{
    param: string
    originalLength: number
    truncatedLength: number
  }>
}

/**
 * Sanitizes query parameters, returning sanitized values and modification info
 *
 * @param query - Object containing query parameters
 * @returns Sanitization result with sanitized params, modification flag, and violations
 */
export function sanitizeQueryParams(
  query: Record<string, string | string[]>,
): SanitizeQueryParamsResult {
  const sanitized: Record<string, string | string[]> = {}
  let modified = false
  const violations: Array<{ param: string; originalLength: number; truncatedLength: number }> = []

  for (const [key, value] of Object.entries(query)) {
    const maxLength = getMaxLengthForParam(key)

    if (Array.isArray(value)) {
      // Handle array values
      const sanitizedArray: string[] = []

      for (const v of value) {
        if (typeof v === 'string') {
          const sanitizedValue = sanitizeTrackingParam(v, maxLength)

          if (sanitizedValue && sanitizedValue.length !== v.length) {
            modified = true
            violations.push({
              param: key,
              originalLength: v.length,
              truncatedLength: sanitizedValue.length,
            })
          }

          sanitizedArray.push(sanitizedValue || '')
        } else {
          sanitizedArray.push(v)
        }
      }

      sanitized[key] = sanitizedArray
    } else if (typeof value === 'string') {
      // Handle string values
      const sanitizedValue = sanitizeTrackingParam(value, maxLength)

      if (sanitizedValue && sanitizedValue.length !== value.length) {
        modified = true
        violations.push({
          param: key,
          originalLength: value.length,
          truncatedLength: sanitizedValue.length,
        })
      }

      sanitized[key] = sanitizedValue || value
    } else {
      // Pass through other types unchanged
      sanitized[key] = value
    }
  }

  return { sanitized, modified, violations }
}

/**
 * Logs security warnings for oversized parameters
 * Note: Implementation varies between client (console) and server (logger)
 *
 * @param params - Object containing parameters to check
 * @param maxLength - Maximum allowed length
 */
export function warnOversizedParams(params: Record<string, unknown>, maxLength: number): void {
  const oversized = Object.entries(params).filter(
    ([, value]) => typeof value === 'string' && value.length > maxLength,
  )

  if (oversized.length > 0) {
    console.warn(
      '[Security] Detected oversized tracking parameters (potential DoS attack):',
      oversized.map(([key, value]) => ({
        key,
        originalLength: typeof value === 'string' ? value.length : 0,
        maxAllowed: maxLength,
        truncated: true,
      })),
    )
  }
}
